import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { NavigationMenu, NavigationMenuContent, NavigationMenuItem, NavigationMenuLink, NavigationMenuList, NavigationMenuTrigger } from "@/components/ui/navigation-menu";
import { cn } from "@/lib/utils";
import { ThemeToggle } from "@/components/ThemeToggle";
const services = [{
  title: "Founder Coaching",
  href: "/services/coaching",
  description: "1:1 coaching for founders and CEOs at Seed–Series B"
}, {
  title: "Keynotes",
  href: "/services/keynotes",
  description: "Inspiring talks for conferences and corporate events"
}, {
  title: "Rapid Response",
  href: "/services/rapid-response",
  description: "Crisis intervention when the house is on fire"
}];
const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const location = useLocation();
  const isActive = (path: string) => location.pathname === path;
  const isServiceActive = services.some((s) => location.pathname === s.href);
  return <header className="fixed top-0 left-0 right-0 z-50 border-b border-border/30 bg-background/70 backdrop-blur-2xl">
      {/* Subtle glow line at bottom */}
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-primary/20 to-transparent" />
      
      <div className="container mx-auto px-6">
        <div className="flex h-16 items-center justify-between">
          {/* Logo with hover effect */}
          <Link to="/" className="flex items-center gap-2 group" aria-label="Burgerc_ - Return to homepage">
            <motion.span className="text-xl font-bold tracking-tight" whileHover={{
            scale: 1.02
          }} transition={{
            type: "spring",
            stiffness: 400,
            damping: 17
          }}>Burgerc_<span className="text-primary animate-cursor-blink pointer-events-none">_</span>
            </motion.span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-1">
            

            <NavigationMenu>
              <NavigationMenuList>
                <NavigationMenuItem>
                  <NavigationMenuTrigger className={cn("bg-transparent", isServiceActive ? "text-foreground" : "text-muted-foreground")}>
                    Services
                  </NavigationMenuTrigger>
                  <NavigationMenuContent>
                    <ul className="grid w-[400px] gap-2 p-4">
                      {services.map((service) => <li key={service.href}>
                          <NavigationMenuLink asChild>
                            <Link to={service.href} className={cn("block select-none rounded-md p-3 leading-none no-underline outline-none transition-colors", "hover:bg-secondary hover:text-foreground", isActive(service.href) ? "bg-secondary text-foreground" : "text-muted-foreground")}>
                              <div className="text-sm font-medium leading-none mb-1">
                                {service.title}
                              </div>
                              <p className="line-clamp-2 text-xs text-muted-foreground">
                                {service.description}
                              </p>
                            </Link>
                          </NavigationMenuLink>
                        </li>)}
                    </ul>
                  </NavigationMenuContent>
                </NavigationMenuItem>
              </NavigationMenuList>
            </NavigationMenu>

            <Link to="/about" className={cn("relative px-4 py-2 text-sm font-medium transition-colors rounded-md group", isActive("/about") ? "text-foreground" : "text-muted-foreground hover:text-foreground")}>
              About
              <span className={cn("absolute bottom-1 left-1/2 -translate-x-1/2 h-0.5 bg-primary rounded-full transition-all duration-300", isActive("/about") ? "w-4" : "w-0 group-hover:w-4")} />
            </Link>

            <ThemeToggle />

            <Link to="/contact" className="ml-2" aria-label="Get in Touch — contact Bastian Burger for consulting inquiries">
              <Button size="sm" className="font-medium relative overflow-hidden group">
                <span className="relative z-10">Get in Touch</span>
                <div className="absolute inset-0 bg-gradient-to-r from-primary via-primary/80 to-primary opacity-0 group-hover:opacity-100 transition-opacity" />
              </Button>
            </Link>
          </nav>

          {/* Mobile Menu Button */}
          <button className="md:hidden p-2 text-muted-foreground hover:text-foreground" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} aria-label="Toggle menu">
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileMenuOpen && <motion.div initial={{
        opacity: 0,
        height: 0
      }} animate={{
        opacity: 1,
        height: "auto"
      }} exit={{
        opacity: 0,
        height: 0
      }} className="md:hidden border-t border-border bg-background">
            <nav className="container mx-auto px-6 py-4 flex flex-col gap-2">
              <Link to="/" onClick={() => setMobileMenuOpen(false)} className={cn("px-4 py-3 text-sm font-medium rounded-md transition-colors", isActive("/") ? "bg-secondary text-foreground" : "text-muted-foreground hover:bg-secondary hover:text-foreground")}>
                Home
              </Link>

              <div>
                <button onClick={() => setServicesOpen(!servicesOpen)} className={cn("w-full flex items-center justify-between px-4 py-3 text-sm font-medium rounded-md transition-colors", isServiceActive ? "bg-secondary text-foreground" : "text-muted-foreground hover:bg-secondary hover:text-foreground")}>
                  Services
                  <ChevronDown size={16} className={cn("transition-transform", servicesOpen && "rotate-180")} />
                </button>
                <AnimatePresence>
                  {servicesOpen && <motion.div initial={{
                opacity: 0,
                height: 0
              }} animate={{
                opacity: 1,
                height: "auto"
              }} exit={{
                opacity: 0,
                height: 0
              }} className="pl-4 mt-1 flex flex-col gap-1">
                      {services.map((service) => <Link key={service.href} to={service.href} onClick={() => setMobileMenuOpen(false)} className={cn("px-4 py-2 text-sm rounded-md transition-colors", isActive(service.href) ? "bg-secondary text-foreground" : "text-muted-foreground hover:bg-secondary hover:text-foreground")}>
                          {service.title}
                        </Link>)}
                    </motion.div>}
                </AnimatePresence>
              </div>

              <Link to="/about" onClick={() => setMobileMenuOpen(false)} className={cn("px-4 py-3 text-sm font-medium rounded-md transition-colors", isActive("/about") ? "bg-secondary text-foreground" : "text-muted-foreground hover:bg-secondary hover:text-foreground")}>
                About
              </Link>

              <div className="flex items-center justify-between mt-4 pt-4 border-t border-border">
                <span className="text-sm text-muted-foreground">Theme</span>
                <ThemeToggle />
              </div>

              <Link to="/contact" onClick={() => setMobileMenuOpen(false)} className="mt-4" aria-label="Get in Touch — contact Bastian Burger for consulting inquiries">
                <Button className="w-full font-medium">Get in Touch</Button>
              </Link>
            </nav>
          </motion.div>}
      </AnimatePresence>
    </header>;
};
export default Header;